import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Sidebar";
import MonthlySalesComparison from "./MonthSalesComparison";
import DailyComparisonChart from "./DailyComparisonChart";
import WeekComparisonChart from "./WeekComparisonChart";
import WeekDetailsChart from "./WeekDetailsChart";
import QuarterlySalesComparison from "./QuarterlySalesComparison";
import ScatterPlotSales from "./ScatterPlotSales";
import TabelaPorownaniaMiesiecy from "./TabelaPorownaniaMiesiecy";
import { useLocation } from "react-router-dom";
import { FaChartLine } from "react-icons/fa";
import IdentyfikacjaNietypowychTransakcji from "./IdentyfikacjaNietypowychTransakcji";
import StrukturaSprzedazyChart from "./StrukturaSprzedazyChart";
import "./DashboardGrid.css";
import axios from "axios";
import AverageSalesByWeekday from "./AverageSalesByWeekday";
import { LayoutDashboard} from "lucide-react";
import { getDB, dbGet, dbPut } from "../../db";
import styled, { createGlobalStyle } from "styled-components";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import TopBar from "../TopBar";
import MonthlySalesByYearChart from "./MonthlySalesByYearChart";
import WeeklySalesComparisonWithTrends from "./WeeklySalesComparisonWithTrends";
import { DownloadButtons } from "../DownloadButtons";
import { jwtDecode } from "jwt-decode";

// === NEW: Import license-gated buttons ===

/* ----------------------- EXPORT MODE (generic) ----------------------- */
const ExportModeStyles = createGlobalStyle`
  /* Disables hover/interaction during capture */
  .for-export * {
    pointer-events: none !important;
    cursor: default !important;
  }
  /* Optional: hide any element you mark explicitly */
  .for-export [data-export-hide="true"] {
    display: none !important;
  }
`;
const enableExportMode = () => document.body.classList.add("for-export");
const disableExportMode = () => document.body.classList.remove("for-export");
/* -------------------------------------------------------------------- */

const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "20px 20px 40px 20px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  textAlign: "center",
};

const formatPath = (path) => {
  const customNames = {
    "sprzedaz/daily": { name: "Sprzedaży / Sprzedaż wg okresów", icon: <FaChartLine /> },
  };
  const cleanPath = path.replace(/^\//, "");
  return customNames[cleanPath] || { name: cleanPath.replace("/", " / "), icon: null };
};

const SectionTitle = ({ title, inGrid = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 20px 12px 20px",
      margin: inGrid ? "8px 0 12px 0" : "8px 20px 12px 20px",
      fontSize: "1.05rem",
      fontWeight: 700,
      color: "#2b2b2b",
      position: "relative",
      background: "linear-gradient(to right, rgba(135,206,235,0.12), rgba(135,206,235,0.04))",
      border: "1px solid rgba(135,206,235,0.35)",
      borderRadius: "8px",
      ...(inGrid ? { gridColumn: "1 / -1", paddingLeft: "20px", paddingRight: "20px" } : {}),
    }}
  >
    <LayoutDashboard size={20} strokeWidth={2} color="#87CEEB" />
    <span style={{ position: "relative", display: "inline-block" }}>
      {title}
      <span
        style={{
          position: "absolute",
          left: 0,
          bottom: -3,
          width: "100%",
          height: 2,
          backgroundColor: "#87CEEB",
          borderRadius: 2,
          opacity: 0.7,
        }}
      />
    </span>
  </div>
);

const SprzedazDzien = () => {
  const [email, setEmail] = useState("");
  const [data, setData] = useState([]);
  const locationData = useLocation();
  const { name, icon } = formatPath(locationData.pathname);
  const [loadingComponent, setLoadingComponent] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  // === NEW: license state ===
const [licenseType, setLicenseType] = useState(null);

  // Refs for all dashboard components
  const monthlySalesRef = useRef(null);
  const quarterlySalesRef = useRef(null);
  const weekDetailsRef = useRef(null);
  const weekComparisonRef = useRef(null);
  const dailyComparisonRef = useRef(null);
  const tabelaRef = useRef(null);
  const monthlySalesByYearRef = useRef(null);
  const nietypoweRef = useRef(null);
  const averageSalesRef = useRef(null);
  const scatterPlotRef = useRef(null);
  const strukturaRef = useRef(null);
  const weeklyTrendsRef = useRef(null);


  function extractLicenseFromToken(token) {
  if (!token) return null;
  try {
    const d = jwtDecode(token) || {};
    const typeRaw =
      d.LicenseType || d.licenseType || d.plan || d.Plan || "";

    const expRaw =
      d.LicenseExpire ?? d.licenseExpire ?? "";

    // detect NO EXPIRE by type, explicit flag, or far-future date
    const t = String(typeRaw).toUpperCase();
    const e = String(expRaw).toUpperCase();

    const typeHasNoExp = t.includes("NO EXPIRE") || t.includes("NO_EXPIRE");
    const expIsFlag    = e === "NO EXPIRE" || e === "NO_EXPIRE";

    let yearIsFar = false;
    if (!expIsFlag && typeof expRaw === "string" && expRaw) {
      const dt = new Date(expRaw);
      yearIsFar = !isNaN(dt) && dt.getUTCFullYear() >= 2099;
    }

    if (typeHasNoExp || expIsFlag || yearIsFar) return "NO EXPIRE";
    return typeRaw || null;
  } catch {
    return null;
  }
}


useEffect(() => {
  const userRaw = localStorage.getItem("user");
  if (userRaw) {
    try {
      const user = JSON.parse(userRaw);
      if (user && user.Email) setEmail(user.Email);

      // try common keys in user
      const ltUser =
        user?.licenseType ??
        user?.LicenseType ??
        user?.plan ??
        user?.Plan ??
        null;

      if (typeof ltUser === "string" && ltUser) {
        setLicenseType(ltUser);
      }
    } catch {}
  }

  // always try token as source of truth
  const token = localStorage.getItem("token");
  const ltFromToken = extractLicenseFromToken(token);
  if (ltFromToken) {
    setLicenseType(ltFromToken);
  }

  // debug: see what ended up set
  setTimeout(() => {
    console.log("[SprzedazDzien] licenseType (final):", ltFromToken);
  }, 0);
}, []);



  const fetchData = async () => {
    try {
      await getDB();
      const cached = await dbGet("sales", "allSales");
      if (cached?.data?.length) {
        setData(cached.data);
        setLoadingComponent("Loaded sales data from local IndexedDB");
        return;
      }
      setLoadingComponent("No cache found. Fetching sales from API...");
      const token = localStorage.getItem("token");
      const res = await axios.get("https://server229256.nazwa.pl/api/sprzedaz", {
        headers: { Authorization: token },
      });
      const sales = res.data || [];
      setData(sales);
      await dbPut("sales", "allSales", { data: sales, timestamp: Date.now() });
      setLoadingComponent("Fetched sales from API and cached in IndexedDB");
    } catch (e) {
      console.error("fetchData error:", e);
      setLoadingComponent("Error loading sales data");
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ------------ helpers for clean exports (hide in-card controls) ------------ */
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const hideCardControlsTemporarily = (rootEl) => {
    const controls = Array.from(
      rootEl.querySelectorAll(
        'input, select, button, [data-export-hide="true"], .filters, .control-row, .toolbar'
      )
    );
    controls.forEach((el) => {
      el.__prevDisplay = el.style.display;
      el.style.display = "none";
    });
    return () => {
      controls.forEach((el) => {
        el.style.display = el.__prevDisplay || "";
        delete el.__prevDisplay;
      });
    };
  };

  const captureElementAsJpeg = async (el) => {
    el.scrollIntoView({ block: "center" });
    const restore = hideCardControlsTemporarily(el);
    await wait(200);
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      ignoreElements: (node) => {
        if (!(node instanceof Element)) return false;
        // allow explicit opt-out
        return node.matches('[data-export-hide="true"]');
      },
    });
    restore();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    return { dataUrl, width: canvas.width, height: canvas.height };
  };
  /* -------------------------------------------------------------------------- */

  // You asked for default "full" layout. Change to "landscape" per item if needed.
  const COMPONENTS_FOR_REPORT = [
    { ref: monthlySalesRef,   title: "Monthly Sales Comparison",   layout: "landscape-3col" },
    { ref: quarterlySalesRef, title: "Quarterly Sales Comparison", layout: "landscape-3col" },
    { ref: weekDetailsRef,    title: "Week Details Chart",         layout: "landscape-3col" },
    { ref: weekComparisonRef,  title: "Week Comparison Chart",  layout: "landscape-2col" },
    { ref: dailyComparisonRef, title: "Daily Comparison Chart", layout: "landscape-2col" },
    { ref: tabelaRef, title: "Monthly Comparison Table", layout: "landscape-2col" },
    { ref: monthlySalesByYearRef, title: "Monthly Sales by Year", layout: "landscape-2col" },
    { ref: nietypoweRef, title: "Anomaly Detection", layout: "landscape-2col" },
    { ref: averageSalesRef, title: "Average Sales by Weekday", layout: "landscape-2col" },
    { ref: scatterPlotRef, title: "Scatter Plot of Sales", layout: "landscape" },
    { ref: strukturaRef, title: "Sales Structure (Kraj/UE/Eksport)", layout: "landscape-2col" },
    { ref: weeklyTrendsRef, title: "Weekly Sales with Trends", layout: "landscape-2col" },
  ];

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const reportTitle = "Sales by Periods — Report";

      // Portrait & Landscape margins
      const P_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
      const L_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
      const pages = [];

      const getPageMetrics = (orient) => {
        const isLandscape = orient === "l" || orient === "l2" || orient === "l3";
        const base = isLandscape
          ? { pageW: 297, pageH: 210, margins: L_MARGINS }
          : { pageW: 210, pageH: 297, margins: P_MARGINS };
        const contentW = base.pageW - base.margins.left - base.margins.right;
        const contentH = base.pageH - base.margins.top - base.margins.bottom;
        return { ...base, contentW, contentH };
      };

      let curY = null;
      const ensurePortraitPage = () => {
        if (!pages.length || pages[pages.length - 1].orient !== "p") {
          pages.push({ orient: "p", elements: [] });
          const { margins } = getPageMetrics("p");
          curY = margins.top;
        }
      };

      let l3colCountOnCurrentPage = 0;
      let l2colCountOnCurrentPage = 0;

      const addBlock = (img, title, layout) => {
        const gutter = 6;

        // 3-col landscape
        if (layout === "landscape-3col") {
          if (!pages.length || pages[pages.length - 1].orient !== "l3" || l3colCountOnCurrentPage >= 3) {
            pages.push({ orient: "l3", elements: [] });
            l3colCountOnCurrentPage = 0;
          }
          const { margins, contentW, contentH } = getPageMetrics("l");
          const columns = 3;
          const cellW = (contentW - gutter * (columns - 1)) / columns;
          const cellH = contentH * 0.9;
          const col = l3colCountOnCurrentPage;
          const x = margins.left + col * (cellW + gutter);
          const y = margins.top;
          const ratio = img.height / img.width;
          let w = cellW;
          let h = w * ratio;
          if (h > cellH) {
            h = cellH;
            w = h / ratio;
          }
          pages[pages.length - 1].elements.push({
            x,
            y: y + (cellH - h) / 2,
            w,
            h,
            title,
            dataUrl: img.dataUrl,
          });
          l3colCountOnCurrentPage += 1;
          return;
        }

        // 2-col landscape
        if (layout === "landscape-2col") {
          if (!pages.length || pages[pages.length - 1].orient !== "l2" || l2colCountOnCurrentPage >= 2) {
            pages.push({ orient: "l2", elements: [] });
            l2colCountOnCurrentPage = 0;
          }
          const { margins, contentW, contentH } = getPageMetrics("l");
          const columns = 2;
          const cellW = (contentW - gutter * (columns - 1)) / columns;
          const cellH = contentH * 0.9;
          const col = l2colCountOnCurrentPage;
          const x = margins.left + col * (cellW + gutter);
          const y = margins.top;
          const ratio = img.height / img.width;
          let w = cellW;
          let h = w * ratio;
          if (h > cellH) {
            h = cellH;
            w = h / ratio;
          }
          pages[pages.length - 1].elements.push({
            x,
            y: y + (cellH - h) / 2,
            w,
            h,
            title,
            dataUrl: img.dataUrl,
          });
          l2colCountOnCurrentPage += 1;
          return;
        }

        // single landscape
        if (layout === "landscape") {
          pages.push({ orient: "l", elements: [] });
          const { margins, contentW, contentH } = getPageMetrics("l");
          const ratio = img.height / img.width;
          const targetW = contentW;
          const targetH = Math.min(targetW * ratio, contentH * 0.9);
          const startY = margins.top + (contentH - targetH) / 2;
          pages[pages.length - 1].elements.push({
            x: margins.left,
            y: startY,
            w: targetW,
            h: targetH,
            title,
            dataUrl: img.dataUrl,
          });
          return;
        }

        // portrait full flow
        const { margins, contentW, contentH } = getPageMetrics("p");
        const targetW = contentW;
        const ratio = img.height / img.width;
        const targetH = Math.min(targetW * ratio, contentH * 0.9);
        if (!pages.length || pages[pages.length - 1].orient !== "p") {
          pages.push({ orient: "p", elements: [] });
          const { margins: m } = getPageMetrics("p");
          curY = m.top;
        }
        if (curY + targetH > margins.top + contentH) {
          pages.push({ orient: "p", elements: [] });
          curY = margins.top;
        }
        pages[pages.length - 1].elements.push({
          x: margins.left,
          y: curY,
          w: targetW,
          h: targetH,
          title,
          dataUrl: img.dataUrl,
        });
        curY += targetH + 6;
      };

      // Capture and add all components
      for (const block of COMPONENTS_FOR_REPORT) {
        if (!block.ref?.current) continue;
        const img = await captureElementAsJpeg(block.ref.current);
        addBlock(img, block.title, block.layout);
      }

      if (pages.length === 0) pages.push({ orient: "p", elements: [] });

      // FIRST PAGE orientation (treat l/l2/l3 as landscape)
      const firstOrient = pages[0].orient === "p" ? "portrait" : "landscape";
      const pdf = new jsPDF({
        orientation: firstOrient,
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const drawHeaderFooter = (pdf, page, pageCount, opts) => {
        const { leftMargin, topMargin, rightMargin, bottomMargin, title } = opts;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(title, leftMargin, topMargin - 5, { baseline: "bottom" });
        pdf.setLineWidth(0.2);
        pdf.line(
          leftMargin,
          topMargin - 3,
          pdf.internal.pageSize.getWidth() - rightMargin,
          topMargin - 3
        );
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const pageLabel = `Page ${page} / ${pageCount}`;
        const dateLabel = new Date().toISOString().slice(0, 19).replace("T", " ");
        const y = pdf.internal.pageSize.getHeight() - bottomMargin + 5;
        pdf.text(pageLabel, leftMargin, y);
        const w = pdf.getTextWidth(dateLabel);
        pdf.text(dateLabel, pdf.internal.pageSize.getWidth() - rightMargin - w, y);
      };

      const pageCount = pages.length;
      pages.forEach((pg, i) => {
        if (i > 0) {
          const ori = pg.orient === "p" ? "portrait" : "landscape"; // 'l', 'l2', 'l3' => landscape
          pdf.addPage("a4", ori);
        }
        const { margins } = getPageMetrics(pg.orient);
        drawHeaderFooter(pdf, i + 1, pageCount, {
          leftMargin: margins.left,
          rightMargin: margins.right,
          topMargin: margins.top,
          bottomMargin: margins.bottom,
          title: "Sales by Periods — Report",
        });
        pg.elements.forEach((el) => {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10);
          const titleY = el.y - 2;
          if (titleY > margins.top) pdf.text(el.title, el.x, titleY);
          pdf.addImage(el.dataUrl, "JPEG", el.x, el.y, el.w, el.h, undefined, "FAST");
        });
      });

      pdf.save(`Sprzedaz_wg_okresow_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("An error occurred while generating the PDF.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  const handleDownloadPNG = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const zip = new JSZip();

      for (const c of COMPONENTS_FOR_REPORT) {
        if (!c.ref?.current) continue;
        c.ref.current.scrollIntoView({ block: "center" });
        const restore = hideCardControlsTemporarily(c.ref.current);
        await wait(200);
        const canvas = await html2canvas(c.ref.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          ignoreElements: (node) => {
            if (!(node instanceof Element)) return false;
            return node.matches('[data-export-hide="true"]');
          },
        });
        restore();

        const imgData = canvas.toDataURL("image/png");
        zip.file(`${c.title.replace(/\s+/g, "_")}.png`, imgData.split("base64,")[1], {
          base64: true,
        });
      }

      const zipContent = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipContent);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Sprzedaz_wg_okresow_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error generating PNGs:", error);
      alert("Wystąpił błąd podczas generowania PNG.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  return (
    <>
      <ExportModeStyles />

      <div style={{ display: "flex", height: "100vh" }}>
        <Sidebar />

        {/* RIGHT COLUMN */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}>
          <TopBar />

          {/* Page header row (title + actions) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 20px",
              fontSize: "1.2rem",
              fontWeight: "bold",
              color: "#87CEEB",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {icon}
              {name}
            </div>

            {/* === NEW: license-gated buttons === */}
            <div style={{ display: "flex", gap: "10px" }}>
              <DownloadButtons
                licenseType={licenseType}
                onDownloadPDF={handleDownloadPDF}
                onDownloadPNG={handleDownloadPNG}
              />
            </div>
          </div>

          {/* Subheader line (kept for layout consistency) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              marginTop: "2px",
              color: "#444",
              fontSize: "1rem",
              padding: "5px 20px 10px 20px",
              maxWidth: "90%",
              lineHeight: 1.5,
            }}
          />

          {/* Scrollable content */}
          <div style={{ flex: 1, overflow: "auto", padding: "20px", backgroundColor: "#f5f5f5" }}>
            <SectionTitle
              inGrid
              title="Przegląd sprzedaży według miesięcy, tygodni i dni – porównania okresowe, analiza sezonowości i wykrywanie anomalii."
            />

            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <div
                className="card-span-4"
                style={{
                  ...cardStyle,
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                ref={monthlySalesRef}
              >
                <MonthlySalesComparison data={data} />
              </div>

              <div className="card-span-4" style={cardStyle} ref={quarterlySalesRef}>
                <QuarterlySalesComparison data={data} />
              </div>

              <div className="card-span-4" style={cardStyle} ref={weekDetailsRef}>
                <WeekDetailsChart data={data} />
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="card-span-6" style={cardStyle} ref={weekComparisonRef}>
                <WeekComparisonChart data={data} />
              </div>

              <div className="card-span-6" style={cardStyle} ref={dailyComparisonRef}>
                <DailyComparisonChart data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Szczegółowa analiza miesięcy w danym roku, najlepsze i najgorsze miesiące roku, wraz z porównaniem danego miesiąca z poprzednim (m/m) i z tym samym miesiącem w poprzednim roku."
              />

              <div className="card-span-6" style={cardStyle} ref={tabelaRef}>
                <TabelaPorownaniaMiesiecy data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={monthlySalesByYearRef}>
                <MonthlySalesByYearChart data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Porównaj wartości dzienne sprzedaży ze średnią wg tygodnia i dnia, obserwuj transakcje znacząco odbiegające od średniej."
              />

              <div className="card-span-6" style={cardStyle} ref={nietypoweRef}>
                <IdentyfikacjaNietypowychTransakcji data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={averageSalesRef}>
                <AverageSalesByWeekday data={data} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={scatterPlotRef}>
                <ScatterPlotSales data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Struktura sprzedaży (Kraj / UE / Eksport) w ujęciu miesięcznym i tygodniowym"
              />

              <div className="card-span-6" style={cardStyle} ref={strukturaRef}>
                <StrukturaSprzedazyChart data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={weeklyTrendsRef}>
                <WeeklySalesComparisonWithTrends data={data} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDownloading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#333",
            zIndex: 9999,
          }}
        >
          Poczekaj, trwa generowanie raportu... ⏳
        </div>
      )}
    </>
  );
};

export default SprzedazDzien;
